import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Info, X } from "lucide-react";

import { cn } from "@/lib/utils";

const STORAGE_PREFIX = "gestionalo.bannerDismissed";

function getStorageKey(bannerId: string, userId: string): string {
  return `${STORAGE_PREFIX}.${bannerId}.${userId}`;
}

export interface DismissibleBannerProps {
  id: string;
  userId: string;
  message: React.ReactNode;
  ctaLabel?: string;
  ctaTo?: string;
  /**
   * An EXTERNAL destination, rendered as a plain anchor instead of a router
   * `Link`.
   *
   * SEPARATE FROM `ctaTo` RATHER THAN OVERLOADING IT. `ctaTo` goes through
   * react-router, which treats its value as a path — handing it an
   * `https://` URL produces a route nobody has, not a navigation. The two
   * destinations are different kinds of thing, so they get different props
   * and the component picks the right element. `ctaTo` wins if both are
   * passed, because an internal route is the safer default.
   */
  ctaHref?: string;
  variant?: "info";
}

export function DismissibleBanner({
  id,
  userId,
  message,
  ctaLabel,
  ctaTo,
  ctaHref,
  variant = "info",
}: DismissibleBannerProps): JSX.Element | null {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const key = getStorageKey(id, userId);
    try {
      const stored = localStorage.getItem(key);
      if (stored === "1") setDismissed(true);
    } catch {
      // keep showing
    }
  }, [id, userId]);

  const handleDismiss = useCallback(() => {
    const key = getStorageKey(id, userId);
    try {
      localStorage.setItem(key, "1");
      setDismissed(true);
    } catch {
      setDismissed(true);
    }
  }, [id, userId]);

  if (dismissed) return null;

  const isInfo = variant === "info";

  return (
    <div
      role="status"
      className={cn(
        "flex items-center justify-between gap-3 border-b px-4 py-2 text-sm",
        isInfo &&
          "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Info
          className={cn("h-4 w-4 shrink-0", isInfo && "text-blue-600 dark:text-blue-400")}
          aria-hidden
        />
        <p className="min-w-0 font-medium">{message}</p>
        {ctaLabel && ctaTo && (
          <Link
            to={ctaTo}
            className={cn(
              "shrink-0 font-medium underline-offset-2 transition-colors duration-150 hover:underline",
              isInfo &&
                "text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200"
            )}
          >
            {ctaLabel}
          </Link>
        )}
        {ctaLabel && !ctaTo && ctaHref && (
          /*
            `rel="noreferrer"` alongside `noopener` because this points at an
            app store, and the referrer would otherwise leak which page of the
            portal the person was on when they tapped it.
          */
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "shrink-0 font-medium underline-offset-2 transition-colors duration-150 hover:underline",
              isInfo &&
                "text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200"
            )}
          >
            {ctaLabel}
          </a>
        )}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded p-1 transition-colors hover:bg-black/10 dark:hover:bg-white/10"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
